import { useMemo, useState } from "react";
import ConditionalSpinner from "../../../../components/ConditionalSpinner";
import { useGetAnalysersConfigQuery, useGetAnalyticsMetadataQuery } from "../../../../store/api/kinesense";
import { Analyser, AnalyserConfigOptionValue } from "../../../../models/analysis/Config";
import SourceSelectionPage from "./pages/SourceSelectionPage/SourceSelectionPage";
import AlgorithmSelectionPage from "./pages/AlgorithmSelectionPage/AlgorithmSelectionPage";
import NameAndDatePage from "./pages/NameAndDatePage/NameAndDatePage";
import MetadataEntryPage, { ImportMetadataResults } from "./pages/MetadataEntryPage/MetadataEntryPage";
import SummaryPage from "./pages/SummaryPage/SummaryPage";
import { Button, Card, Container } from "react-bootstrap";
import { UploadUtil, UploadUtilOptions } from "../fileUpload/UploadUtil";
import { IntegrationMediaSource, MediaSource } from "../../../../models/media/MediaSource";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs, { Dayjs } from "dayjs";

interface ImportWizardPage {
    id: PageTypes;
    title: string;
    component: JSX.Element;
}
export interface ImportWizardPagePropsBase {
    validityChanged: (validity: boolean) => void;
    dataChanged: (data: unknown) => void;
}

export class ImportRequestPageData<T> {
    isValid: boolean;
    data: undefined | T;

    constructor() {
        this.isValid = false;
        this.data = undefined;
    }
}
export type NameAndDatePageData = {
    name: string;
    startTime: Dayjs;
    timezoneOffset: number;
    hasSetNameManually: boolean;
};

export type ExtraSourceFileData = {
    isLoadingFileData: boolean;
    start: Dayjs | undefined;
    duration: number;
    width: number;
    height: number;
    hasAudioTrack: boolean;
};
export type SourceFile = {
    file: File;
} & ExtraSourceFileData;

export type SourceSelectionPageFilesData = {
    type: "file";
    sourceFiles: SourceFile[];
};
export type SourceSelectionPageLibraryData = {
    type: "library";
    mediaSource: MediaSource;
};
export type SourceSelectionPageIntegrationData = {
    type: "integration";
    integrationMediaSource: IntegrationMediaSource;
};
export type SourceSelectionPageData =
    | SourceSelectionPageFilesData
    | SourceSelectionPageIntegrationData
    | SourceSelectionPageLibraryData;

export type AlgorithmOptionsData = {
    analyserOptions: { [key: string]: { [key: string]: AnalyserConfigOptionValue } };
    standardOptions: { [key: string]: AnalyserConfigOptionValue };
};
export type AlgorithmPageData = Analyser & AlgorithmOptionsData;

export type PageTypes = "source" | "algorithm" | "options" | "name" | "metadata" | "summary";
export type ImportRequestState = {
    source: ImportRequestPageData<SourceSelectionPageData>;
    algorithm: ImportRequestPageData<AlgorithmPageData>;
    name: ImportRequestPageData<NameAndDatePageData>;
    metadata: ImportRequestPageData<ImportMetadataResults>;
    summary: ImportRequestPageData<unknown>;
};

function getDefaultRequestState(): ImportRequestState {
    return {
        source: new ImportRequestPageData<SourceSelectionPageData>(),
        algorithm: new ImportRequestPageData(),
        name: new ImportRequestPageData<NameAndDatePageData>(),
        metadata: new ImportRequestPageData<ImportMetadataResults>(),
        summary: new ImportRequestPageData(),
    };
}

function ImportWizard() {
    const { data: analyserConfig, isLoading: isLoadingAnalysers } = useGetAnalysersConfigQuery();
    const { data: analyserMetadataConfig, isLoading: isLoadingMetadata } = useGetAnalyticsMetadataQuery({});
    const isLoading = isLoadingMetadata || isLoadingAnalysers;

    const [currentPage, setCurrentPage] = useState(0);
    const [uploader, setUploader] = useState<UploadUtil | undefined>(undefined);
    const [hideNavButtons, setHideNavButtons] = useState(false);

    const [request, setRequest] = useState<ImportRequestState>(getDefaultRequestState());

    function resetImportWizard() {
        setCurrentPage(0);
        setUploader(undefined);
        setHideNavButtons(false);
        setRequest(getDefaultRequestState());
    }

    const setValidity = (page: PageTypes, validity: boolean) => {
        const newRequest = { ...request };
        newRequest[page].isValid = validity;
        setRequest(newRequest);
    };

    const setData = (page: PageTypes, data: unknown) => {
        const newRequest = { ...request };
        newRequest[page].data = data;
        setRequest(newRequest);
    };

    const propsForPage = (page: PageTypes): ImportWizardPagePropsBase => {
        const props = {
            validityChanged: (validity) => {
                setValidity(page, validity);
            },
            dataChanged: (data) => {
                setData(page, data);
            },
        };

        // Reset all data if the source changes
        if (page === "source") {
            props.dataChanged = (data: SourceSelectionPageData) => {
                setRequest((old) => {
                    const reset = getDefaultRequestState();
                    delete reset.source;
                    old.source.data = data;

                    // Only reset media source name (for multi-file import) if the user hasn't manually modified it
                    if (
                        old.name?.data?.hasSetNameManually &&
                        old.name.data.name &&
                        data.type === "file" &&
                        data.sourceFiles?.length
                    ) {
                        reset.name.data = {
                            hasSetNameManually: true,
                            name: old.name.data.name,
                            timezoneOffset: undefined,
                            startTime: undefined,
                        };
                    }

                    old = { ...reset, source: old.source };
                    return old;
                });
            };
        }

        return props;
    };

    const handleGetUploader = () => {
        return uploader;
    };

    const handleCreateUploader = (options: UploadUtilOptions) => {
        const originalOnStart = options.onStart;
        options.onStart = () => {
            originalOnStart?.();
        };

        const fileUploader = new UploadUtil(options);
        setUploader(fileUploader);

        return fileUploader;
    };

    function getDefaultFileNameAndStartTime() {
        let fileName: string | undefined = undefined;
        let start: Dayjs | undefined = undefined;
        switch (request.source.data?.type) {
            case "file": {
                const sourceFile = request.source.data.sourceFiles?.[0];
                fileName = sourceFile?.file.name;
                start = sourceFile?.start ?? dayjs.utc(sourceFile?.file.lastModified);
                break;
            }
            case "library":
                fileName = request.source.data.mediaSource?.name;
                start = dayjs.utc(request.source.data.mediaSource?.startsAt);
                break;
            case "integration":
                fileName = request.source.data.integrationMediaSource?.name;
                start = dayjs.utc(request.source.data.integrationMediaSource?.createdAt);
                break;
        }

        return { defaultFileName: fileName ?? "Untitled", defaultStartTime: start };
    }

    const videoFilePreviewUrl = useMemo(() => {
        if (request?.source?.data?.type !== "file") {
            return undefined;
        } else {
            return request.source.data.sourceFiles?.[0]?.file.name !== undefined
                ? URL.createObjectURL(request.source.data.sourceFiles[0].file)
                : undefined;
        }
    }, [request?.source?.data?.type == "file" && request.source.data.sourceFiles?.[0]?.file.name]);

    const pages: ImportWizardPage[] = [
        {
            id: "source",
            title: "Select Data Source",
            component: <SourceSelectionPage value={request.source.data} {...propsForPage("source")} />,
        },
        {
            id: "algorithm",
            title: "Select Algorithm",
            component: (
                <AlgorithmSelectionPage
                    value={request.algorithm.data}
                    analyserConfig={analyserConfig}
                    previewImgUrl={
                        request.source.data?.type === "integration"
                            ? request.source.data.integrationMediaSource?.preview?.source
                            : undefined
                    }
                    previewVidUrl={videoFilePreviewUrl}
                    {...propsForPage("algorithm")}
                />
            ),
        },
        {
            id: "name",
            title: "Name & Time",
            component: (
                <NameAndDatePage
                    value={request.name.data}
                    {...getDefaultFileNameAndStartTime()}
                    {...propsForPage("name")}
                />
            ),
        },
        {
            id: "metadata",
            title: "Information",
            component: (
                <MetadataEntryPage
                    analyserMetadata={analyserMetadataConfig}
                    value={request.metadata.data}
                    {...propsForPage("metadata")}
                />
            ),
        },
        {
            id: "summary",
            title: "Summary",
            component: (
                <SummaryPage
                    request={request}
                    getUploader={handleGetUploader}
                    createUploader={handleCreateUploader}
                    hideNavButtons={hideNavButtons}
                    setHideNavButtons={setHideNavButtons}
                    resetImportWizard={resetImportWizard}
                />
            ),
        },
    ];

    const handleNextClick = () => {
        const nextPage = Math.min(pages.length - 1, currentPage + 1);
        setCurrentPage(nextPage);
    };

    const handleBackClick = () => {
        const nextPage = Math.max(0, currentPage - 1);
        setCurrentPage(nextPage);
    };

    const nextButtonProps = {
        disabled: !request[pages[currentPage].id].isValid || currentPage >= pages.length - 1 || hideNavButtons,
        onClick: handleNextClick,
        className: currentPage >= pages.length - 1 ? "opacity-0" : "",
    };

    return (
        <Container style={{ maxWidth: "70rem" }}>
            <Card className="import-wizard">
                <Card.Header className="gap-4 py-2 px-4 d-flex justify-content-between align-items-center">
                    <h4 className="m-0 d-flex align-items-center">{pages[currentPage].title}</h4>
                    <Button variant="outline-primary" size="sm" title="Next" {...nextButtonProps}>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <ConditionalSpinner isLoading={isLoading}>{pages[currentPage].component}</ConditionalSpinner>
                </Card.Body>
                <Card.Footer>
                    <div className={`footer d-flex justify-content-between gap-2 ${hideNavButtons ? "opacity-0" : ""}`}>
                        <Button
                            variant="secondary"
                            disabled={currentPage <= 0 || hideNavButtons}
                            className={currentPage <= 0 ? "opacity-0" : ""}
                            onClick={handleBackClick}
                        >
                            Back
                        </Button>
                        <Button {...nextButtonProps}>Next</Button>
                    </div>
                </Card.Footer>
            </Card>
        </Container>
    );
}

export default ImportWizard;
